/**
 * RequireCap — engine-aware capability gate for UI elements.
 *
 * Reads from useEffectivePermissions() (which itself caches
 * /api/me/effective-permissions). Renders children only when the current user
 * has the requested capability on the requested module. Useful for *any* UI
 * element that should be conditional on the access engine, not just buttons —
 * works for form fields, sections, tabs, table columns, etc.
 *
 * Backend is the authoritative guardrail (engine-aware require_roles still
 * returns 403 if the user lacks the cap). This component is purely UX so
 * users don't see actions they can't perform.
 *
 *   <RequireCap module="fleet" action="add">
 *     <Button>Add Vehicle</Button>
 *   </RequireCap>
 *
 *   <RequireCap module="employees" action="edit" mode="disable">
 *     <Input value={form.first_name} onChange={...} />
 *   </RequireCap>
 *
 *   <RequireCap module="employees" action="edit" fallback={<span>{value}</span>}>
 *     <Input value={value} onChange={...} />
 *   </RequireCap>
 *
 *   <RequireCap module="fleet" actions={['add','edit']} any>
 *     ...
 *   </RequireCap>
 *
 *   const canEdit = useCap('marketing', 'edit');
 *
 * Props:
 *   module    (string, required)   Module key, e.g. "fleet" | "employees"
 *   action    (string)             Single capability: "view"|"add"|"edit"|"delete"|"approve"|"export"
 *   actions   (string[])           Multiple actions. Default behaviour = ALL must pass.
 *   any       (bool)               With `actions`, pass if ANY one is granted.
 *   mode      ("hide"|"disable")   "hide" (default) = render nothing when denied.
 *                                  "disable" = clone child element(s) adding `disabled` + `readOnly`.
 *   fallback  (ReactNode)          What to render when denied (overrides `mode`).
 *   loading   (ReactNode)          What to render while engine perms haven't loaded yet.
 *                                  Default: hidden until loaded (avoids a button-flash).
 */
import React from 'react';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';

export function useCap(module, action) {
  const perms = useEffectivePermissions();
  // Admin & not-yet-loaded special cases handled inside perms.can()
  return perms.can(module, action);
}

export function useCaps(module, ...actions) {
  const perms = useEffectivePermissions();
  return actions.reduce((acc, a) => ({ ...acc, [a]: perms.can(module, a) }), {});
}

function evalAccess(perms, module, action, actions, any) {
  const checks = action ? [action] : (actions || []);
  if (!checks.length) return true; // no constraint specified → pass-through
  if (any) return checks.some(a => perms.can(module, a));
  return checks.every(a => perms.can(module, a));
}

export default React.forwardRef(function RequireCap({
  module,
  action,
  actions,
  any = false,
  mode = 'hide',
  fallback,
  loading = null,
  children,
  ...rest  // Radix `asChild` parents (DialogTrigger, PopoverTrigger, etc.) inject
           // onClick / onPointerDown / aria-* / data-* here. Forward them onto the
           // single child element so the wrapper is transparent to the parent.
}, ref) {
  const perms = useEffectivePermissions();

  // While permissions are still resolving, avoid a flash of action UI for
  // non-admin users. Admins always pass perms.can() so they render immediately.
  if (!perms.loaded && perms.role !== 'admin') {
    return loading;
  }

  const granted = evalAccess(perms, module, action, actions, any);
  if (granted) {
    // Transparent forwarding: when used as a single-element wrapper (which is
    // the dominant case — gating a Button/Input/etc.), clone the child and
    // inject the ref + any props the parent forwarded to us. This is the same
    // strategy Radix's Slot uses and is what makes the component work inside
    // <DialogTrigger asChild>, <PopoverTrigger asChild>, etc.
    if (React.isValidElement(children) && !Array.isArray(children)) {
      const merged = { ...rest };
      if (ref) merged.ref = ref;
      // Merge className if both sides supply one.
      if (rest.className && children.props.className) {
        merged.className = [children.props.className, rest.className].filter(Boolean).join(' ');
      }
      // Compose any prop named on* (onClick, onPointerDown, onMouseEnter, etc.)
      // when both the parent (Radix asChild) and the child supply one — so
      // neither side silently wins. Child's handler fires first; parent's only
      // runs if not preventDefault'd. Future-proof: works for any new on*
      // event a Radix component might inject.
      Object.keys(rest).forEach((key) => {
        if (key.startsWith('on') && typeof rest[key] === 'function' && typeof children.props[key] === 'function') {
          merged[key] = (e) => { children.props[key](e); if (!e?.defaultPrevented) rest[key](e); };
        }
      });
      return React.cloneElement(children, merged);
    }
    return children;
  }

  if (fallback !== undefined) return fallback;

  if (mode === 'disable') {
    // Clone each direct child adding disabled/readOnly. Works for shadcn Button,
    // Input, Textarea, Select trigger, native form elements. Non-element children
    // (strings, numbers) pass through unchanged.
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      return React.cloneElement(child, {
        disabled: true,
        readOnly: true,
        'aria-disabled': true,
        title: child.props.title || 'Read-only — you do not have permission to edit',
        // Visual hint so users see why the control is dim.
        className: [child.props.className, 'opacity-60 cursor-not-allowed'].filter(Boolean).join(' '),
      });
    });
  }

  // Default mode: hide.
  return null;
});
